import { ThemeClassName } from "../utils/theme";

export type AuthSidebarVariant = "default" | "seller";

export interface AuthSidebarQuote {
  text: string;
  attribution: string;
  role?: string;
}

export interface AuthSidebarContent {
  /** Brand homepage — when set, the sidebar logo links here */
  websiteUrl?: string;
  /** Large brand-forward line under the logo */
  headline: string;
  /** Short supporting line under the headline */
  tagline?: string;
  /** Main paragraph */
  body: string;
  /** Optional testimonial — shown above showcase images */
  quote?: AuthSidebarQuote;
  /** Optional deal tombstones / award badges (public/ paths) */
  showcaseImages?: string[];
  /** Optional logo sizing override for wide wordmarks */
  logoWidth?: number;
  logoHeight?: number;
}

export interface AuthSidebarBrand {
  default: AuthSidebarContent;
  seller?: AuthSidebarContent;
}

function isRumaPlatform(): boolean {
  const hostname = window.location.hostname.toLowerCase();
  const rumaDomain = "rumahq.net";
  return hostname === rumaDomain || hostname.endsWith(`.${rumaDomain}`);
}

/** Shared legal / platform footer links shown on every auth sidebar */
export function getAuthFooterLinks() {
  const ruma = isRumaPlatform();

  return {
    privacyUrl: "https://liquidacquire.com/privacy-policy",
    termsUrl: "https://liquidacquire.com/terms-of-service",
    poweredByUrl: ruma ? "https://rumahq.net" : "https://liquidacquire.com",
    poweredByName: ruma ? "Ruma" : "Liquid",
  };
}

const liquidDefault: AuthSidebarContent = {
  headline: "Secure, fast Data Rooms & document sharing",
  body: "Liquid is the Data Room built for tomorrow's dealmakers. Refreshingly straightforward, fast and modern without the baggage (or price tag) of legacy providers.",
};

const itexchangenetDefault: AuthSidebarContent = {
  websiteUrl: "https://itexchangenet.com",
  logoWidth: 160,
  logoHeight: 40,
  headline: "A Global M&A Consultancy",
  tagline: "We Sell IT and Digital Marketing Firms",
  body: "Ranked by Axial as the #1 sell-side technology M&A advisor in the U.S., IT ExchangeNet represents MSPs, MSSPs, VARs, and digital marketing agencies — with a network of more than 90,000 IT and digital marketing decision-makers worldwide.",
  quote: {
    text: "In my opinion, ITX has one of the most comprehensive networks of buyers in the industry for Microsoft Channel Partners. ITX generated interest from nearly 50 qualified buyers.",
    attribution: "Rick Brown",
    role: "Co-Founder & CEO, Rock Solid Technologies",
  },
  showcaseImages: [
    "/itexchangenet-deal-1.jpg",
    "/itexchangenet-deal-2.jpg",
    "/itexchangenet-deal-3.jpg",
  ],
};

const itexchangenetSeller: AuthSidebarContent = {
  ...itexchangenetDefault,
  headline: "Ready for the next step in selling your business?",
  tagline: "The #1 sell-side technology M&A advisor in the U.S.",
  body: "IT ExchangeNet focuses exclusively on selling MSPs, MSSPs, VARs, and digital marketing agencies. From qualification through close, we match you with the right strategic buyers from our global network.",
};

/**
 * Per-theme auth sidebar copy. Add a new entry here when onboarding a brand.
 * Themes without an entry fall back to the default Liquid / Ruma content.
 */
export const authSidebarBrands: Partial<
  Record<ThemeClassName, AuthSidebarBrand>
> = {
  itexchangenet: {
    default: itexchangenetDefault,
    seller: itexchangenetSeller,
  },
  // carson: { default: {...} },
};

const liquidBrand: AuthSidebarBrand = {
  default: liquidDefault,
};

export function getAuthSidebarContent(
  theme: ThemeClassName,
  variant: AuthSidebarVariant = "default",
): AuthSidebarContent {
  const brand = authSidebarBrands[theme] ?? liquidBrand;
  if (variant === "seller") {
    return brand.seller ?? brand.default;
  }
  return brand.default;
}
